// Inbox slot — AI auto-pause on channel-share (US-089, EPIC-07).
//
// Mounted by `<ThreadPanel>` (5c.2) whenever the AI's most-recent outbound
// message contains a calendar / phone / email token. Detection logic lives
// in `lib/modePolicy.ts#detectChannelShareInAiMessage` and is intentionally
// shared with the server-side detector (Wave 6) so client-side optimistic
// UI matches what the server eventually persists.
//
// While locked, `derivePolicy()` returns `effectiveMode: 'paused'` — the
// thread suppresses any new AI draft until the user reports the outcome via
// `<PendingActionCard>` (5c.2 / US-076). Sending a PDF / deck is intentionally
// NOT a trigger — see business "Bon à savoir" footnote at
// `prototypes_and_docs/ls-business.jsx:1245-1251`.

import { useT } from '@barel/i18n';
import { Banner, Button } from '@barel/ui';

export type ChannelShareToken = 'calendar' | 'phone' | 'email';

export interface AIPauseOnSensitiveProps {
  visible: boolean;
  tokens: readonly ChannelShareToken[];
  /** Wires to `<PendingActionCard>` outcome reporter (5c.2). */
  onReportOutcome?: () => void;
}

export function AIPauseOnSensitive({ visible, tokens, onReportOutcome }: AIPauseOnSensitiveProps) {
  const t = useT('inbox');
  if (!visible) return null;

  // Compose the human label list — i18n catalogue owns each token's label.
  const tokenLabels = tokens
    .map((tok) => t(`policy.aiPause.tokens.${tok}`))
    .filter((label) => label && label.length > 0);
  const tokensText =
    tokenLabels.length > 0 ? tokenLabels.join(', ') : t('policy.aiPause.tokens.calendar');

  return (
    <Banner
      data-component="AIPauseOnSensitive"
      data-testid="inbox-ai-pause-banner"
      tone="magenta"
      title={t('policy.aiPause.title')}
      icon="⏸"
      action={
        onReportOutcome ? (
          <Button
            type="button"
            size="sm"
            variant="primary"
            onClick={onReportOutcome}
            data-testid="inbox-ai-pause-report-outcome"
          >
            {t('policy.aiPause.report')}
          </Button>
        ) : undefined
      }
    >
      {t('policy.aiPause.body', { tokens: tokensText })}
    </Banner>
  );
}
